// Reference Libraries — user-record persistence layer.
//
// Storage key: rsc.reflibs.user.v1  (per Module 2 approval; SEED IS NEVER
// PERSISTED — see lib/reflibs/seed.js).
//
// User records override seed records of the same id (see lookup.js).

import { emptyLibraryRecord, validateLibraryRecord, withDerived, CATEGORIES } from "./schema";
import { scoped } from "@/lib/accountScope";

export const USER_STORAGE_KEY = "rsc.reflibs.user.v1";

const _now = () => new Date().toISOString();
const _genId = () =>
  `userref_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

function _readAll() {
  try {
    const raw = scoped.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function _writeAll(rows) {
  scoped.setItem(USER_STORAGE_KEY, JSON.stringify(rows));
}

// Public API --------------------------------------------------------------

export function listUserRecords() {
  return _readAll();
}

export function listUserByCategory(category) {
  if (!CATEGORIES.includes(category)) return [];
  return _readAll().filter((r) => r.category === category);
}

export function getUserRecord(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function createUserRecord(rec) {
  const baked = withDerived({ ...rec, source: rec.source || "user_entered" });
  const v = validateLibraryRecord(baked);
  if (!v.ok) {
    const err = new Error(`Invalid library record: ${v.errors.join(" ")}`);
    err.validationErrors = v.errors;
    throw err;
  }
  const all = _readAll();
  const row = {
    ...baked,
    id:        baked.id && String(baked.id).trim() ? baked.id : _genId(),
    isActive:  baked.isActive == null ? true : !!baked.isActive,
    createdAt: _now(),
    updatedAt: _now(),
  };
  // Guard duplicate ids
  if (all.some((x) => x.id === row.id)) {
    throw new Error(`User record with id ${row.id} already exists.`);
  }
  all.push(row);
  _writeAll(all);
  return row;
}

export function updateUserRecord(id, patch) {
  const all = _readAll();
  const idx = all.findIndex((r) => r.id === id);
  if (idx < 0) throw new Error(`User record not found: ${id}`);
  const merged = withDerived({ ...all[idx], ...patch, id });
  const v = validateLibraryRecord(merged);
  if (!v.ok) {
    const err = new Error(`Invalid library record: ${v.errors.join(" ")}`);
    err.validationErrors = v.errors;
    throw err;
  }
  const next = { ...merged, updatedAt: _now() };
  all[idx] = next;
  _writeAll(all);
  return next;
}

export function deleteUserRecord(id) {
  const all = _readAll();
  const filtered = all.filter((r) => r.id !== id);
  if (filtered.length === all.length) return false;
  _writeAll(filtered);
  return true;
}

/** Soft delete — flips isActive=false but keeps the row. */
export function setUserRecordActive(id, isActive) {
  return updateUserRecord(id, { isActive: !!isActive });
}

/**
 * Clone — creates a brand-new user record from any record (seed or user).
 * Returned record has a fresh user id and `source: "user_entered"`.
 */
export function cloneAsUserRecord(seedOrUserRecord) {
  if (!seedOrUserRecord) throw new Error("cloneAsUserRecord: no source row.");
  const copy = {
    ...emptyLibraryRecord(seedOrUserRecord.category),
    ...seedOrUserRecord,
    id:        _genId(),
    source:    "user_entered",
    notes:     (seedOrUserRecord.notes || "") +
               (seedOrUserRecord.notes ? " " : "") +
               "(cloned)",
    isActive:  true,
    createdAt: _now(),
    updatedAt: _now(),
  };
  // Persist
  const all = _readAll();
  all.push(copy);
  _writeAll(all);
  return copy;
}

// Test / diagnostic
export function _clearAllUserRecords() {
  scoped.removeItem(USER_STORAGE_KEY);
}
